/* eslint-disable @typescript-eslint/ban-types */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as XLSX from "xlsx";
import { SubmitButton as Button } from "~/components/form/submit-button";
import { Link } from "@tanstack/react-router";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import { parseDate } from "~/utils/basic";

const ReportTable = ({
  data,
  fields,
  reportname,
}: {
  data: any[];
  fields: string[];
  reportname: string;
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "", direction: "asc" });
  const [filteredData, setFilteredData] = useState<any[]>(data);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInputs, setSearchInputs] = useState<Record<string, string>>({});

  const columnHelper = createColumnHelper<any>();

  const tableData = useMemo(() => data, [data]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      const regex =
        /^(\d{2})-(\d{2})-(\d{4}) (\d{1,2}):(\d{2}):(\d{2}) (AM|PM)$/i;
      if (typeof aValue === "string" && regex.test(aValue)) {
        aValue = parseDate(aValue);
      }

      if (typeof bValue === "string" && regex.test(bValue)) {
        bValue = parseDate(bValue);
      }

      if (aValue === "" || aValue == null) {
        return 1;
      }
      if (bValue === "" || bValue == null) {
        return -1;
      }
      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedSetFilters = useCallback(
    debounce((value: string, key: string) => {
      setFilters((prev) => ({
        ...prev,
        [key]: [value],
      }));
    }, 300),
    []
  );

  const handleInputChange = useCallback(
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchInputs((prev) => ({
        ...prev,
        [key]: value,
      }));
      debouncedSetFilters(value, key);
    },
    [debouncedSetFilters]
  );

  const handleDownload = async (url: string, name: string) => {
    try {
      console.log(url);
      console.log(name);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();

      // Clean up
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const paginatedData = useMemo(() => {
    if (rowsPerPage === -1) return sortedData;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage]);

  const columns = useMemo(
    () =>
      fields.map((field, index) => {
        const isViewColumn = field === "View";
        const isDownloadColumn = field === "Download";
        const header = (
          <div
            className="p-2"
            style={
              isViewColumn
                ? {
                    position: "sticky",
                    right: 0,
                    backgroundColor: "white", // Optional: Prevents background overlap
                    zIndex: 10, // Ensure it's above other content
                  }
                : undefined // Keep the default behavior if it's not the "View" column
            }
          >
            <div className="flex flex-col gap-2 justify-center">
              <div className="flex gap-2 justify-center">{field}</div>
              {!isViewColumn && !isDownloadColumn && (
                <div className="flex items-center justify-center gap-2">
                  <input
                    id={field}
                    type="text"
                    value={searchInputs[field] || ""}
                    onChange={handleInputChange(field)}
                    className={`border text-sm border-gray-300 rounded ${
                      field === "Service Code"
                        ? "w-[250px]"
                        : field === "Agent Exception Reason"
                          ? "w-[300px]"
                          : "w-[200px]"
                    }`}
                    //placeholder="Search..."
                  />
                  <button
                    onClick={() => {
                      requestSort(field);
                    }}
                  >
                    <ArrowsUpDownIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        );

        const cell = (props: any) =>
          index === fields.length - 1 ? (
            <>
              {reportname === "output_files" ? (
                // This section is shown if the "Report Title" is "Output Files"
                <div>
                  <Button
                    type="button"
                    size="small"
                    variant="secondary"
                    onClick={() => {
                      const outputType = props?.row.original["Output Type"];
                      let fileName = props?.row.original["File Name"];
                      if (outputType === "PE-OTIL") {
                        fileName += ".zip";
                      } else {
                        fileName += ".xml";
                      }
                      handleDownload(props?.row.original["Download"], fileName);
                    }}
                  >
                    <img
                      src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxZW0iIGhlaWdodD0iMWVtIiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxwYXRoIGZpbGw9IiNmMTU2MTMiIGQ9Ik0xOSAxMnY3SDV2LTdIM3Y3YzAgMS4xLjkgMiAyIDJoMTRjMS4xIDAgMi0uOSAyLTJ2LTd6bS02IC42N2wyLjU5LTIuNThMMTcgMTEuNWwtNSA1bC01LTVsMS40MS0xLjQxTDExIDEyLjY3VjNoMnoiLz48L3N2Zz4="
                      alt="Download Icon"
                    />
                  </Button>
                </div>
              ) : (
                // This section is shown if the "Report Title" is not "Output Files"
                <div
                  style={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: "White", // Optional: Prevents background overlap
                    zIndex: 10, // Ensure it's above other content
                  }}
                >
                  {props?.row.original["Task Id"] ? (
                    <Link
                      to="/supervisor/worklist/view/$taskId"
                      params={{ taskId: props?.row.original["Task Id"] }}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <Button
                        type="button"
                        size="small"
                        variant="secondary"
                        className="text-primary ring-primary bg-white hover:bg-orange-100 active:bg-orange-200"
                      >
                        View
                      </Button>
                    </Link>
                  ) : (
                    <Button
                        type="button"
                        size="small"
                        variant="secondary"
                        className="text-primary ring-primary active:bg-orange-200 cursor-not-allowed"
                      >
                        View
                      </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            // Default rendering if the condition does not match
            <div>{props?.getValue()}</div>
          );

        return columnHelper.accessor(field, { header: header as any, cell });
      }),
    [columnHelper, tableData, sortConfig, fields, searchInputs]
  );

  const requestSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const filtered = data.filter((row) =>
      Object.entries(searchInputs).every(([key, searchValues]) => {
        if (!searchValues) {
          return true;
        }
        const values = Array.isArray(searchValues)
          ? searchValues
          : [searchValues];
        return values.some((value) =>
          row[key]?.toString().toLowerCase().includes(value.toLowerCase())
        );
      })
    );
    console.log("Filtered Data on Milestone", filtered);
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page on search change
  }, [filters, data]);

  const tableObj = useReactTable({
    columns,
    data: paginatedData,
    enableRowSelection: true,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
  });

  const exportToExcel = () => {
    // add  one more condition  if col is not equal to Download
    const visibleColumns = fields.filter(
      (col) => col !== "View" && col !== "Download"
    );
    console.log(visibleColumns);
    const filteredData = sortedData.map((row) => {
      const rowData: Record<string, any> = {};
      visibleColumns.forEach((col) => {
        rowData[col] = row[col];
      });
      return rowData;
    });

    // Create the worksheet and workbook for Excel export
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();

    let outputfile_name = "Output Data";
    if (reportname === "invoice_accuracy") {
      outputfile_name = "Invoice Accuracy Report";
    } else if (reportname === "header_accuracy") {
      outputfile_name = "Header Accuracy Report";
    } else if (reportname === "output_files") {
      outputfile_name = "Output Files";
    } else if (reportname === "milestones_dashboard") {
      outputfile_name = "Milestones Dashboard";
    } else if (reportname === "accuracy") {
      outputfile_name = "Accuracy Report";
    } else if (reportname === "accessorial_accuracy"){
      outputfile_name = "Accessorials Accuracy Report";
    } else if (reportname === "reference_accuracy"){
      outputfile_name = "Reference Accuracy Report";
    }
    XLSX.utils.book_append_sheet(workbook, worksheet, outputfile_name);
    XLSX.writeFile(workbook, outputfile_name + ".xlsx");
    // XLSX.utils.book_append_sheet(workbook, worksheet, reportname);
    // XLSX.writeFile(workbook, `${reportname}.xlsx`);
  };
  const hasValidData =
    Array.isArray(sortedData) &&
    sortedData.some((row) =>
      Object.values(row).some(
        (val) => val !== null && val !== "" && val !== undefined
      )
    );
  return (
    <div className="w-full">
      <div className="flex justify-between mb-4">
        <div>
          <label>
            Rows per page:
            <select
              value={rowsPerPage}
              onChange={(e) => {
                const value =
                  e.target.value === "all" ? -1 : parseInt(e.target.value, 10);
                setRowsPerPage(value);
              }}
              className="border rounded p-1 pr-6"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={sortedData.length}>All</option>
            </select>
          </label>
        </div>
        <button
          onClick={exportToExcel}
          disabled={!hasValidData}
          className={`py-2 px-4 rounded font-semibold ${
            !hasValidData
              ? "bg-[#ff7a29] text-white opacity-50 cursor-not-allowed"
              : "bg-[#ff7a29] text-white hover:bg-[#ff6415]"
          }`}
        >
          Export to Excel
        </button>
      </div>
      <div className="overflow-auto max-w-full h-[400px]">
        <table
          className="min-w-full divide-y divide-gray-200 text-center"
          style={{ tableLayout: "fixed" }}
        >
          <thead className="bg-gray-50">
            {tableObj.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    className="border border-neutral-300 capitalize"
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableObj.getRowModel().rows.map((row) => {
              const isFCN =
                row.original["Document Type"]?.toLowerCase() == "fcn";
              const isPOD =
                row.original["Document Type"]?.toLowerCase() == "pod";
              const isExceptionStatus =
                row.original["Status"]?.toLowerCase() == "exception";
              //const receivedOnServerText= row.original["Received On Server"];
              //const agentKeyedOnText= row.original["Agent Keyed On"];
              // const receivedOnServerDate = receivedOnServerText ? new Date(receivedOnServerText.split(' ')[0]) : null;
              // const agentKeyedOnDate = agentKeyedOnText ? new Date(agentKeyedOnText.split(' ')[0]) : null;
              const agingValue = row.original["Aging"];
              return (
                <tr
                  key={row.id}
                  className={
                    isFCN && isExceptionStatus && agingValue < "01:00:00"
                      ? ""
                      : isFCN &&
                          isExceptionStatus &&
                          agingValue >= "01:00:00" &&
                          agingValue < "02:00:00"
                        ? "border-2 border-amber-300 bg-amber-200"
                        : isFCN && isExceptionStatus && agingValue >= "02:00:00"
                          ? "border-2 border-red-300 bg-red-200"
                          : isPOD &&
                              isExceptionStatus &&
                              agingValue >= "24:00:00"
                            ? "border-2 border-red-300 bg-red-200"
                            : "border border-neutral-300"
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={
                        cell.column.id === "View"
                          ? {
                              position: "sticky",
                              right: 0,
                              backgroundColor: "white",
                              zIndex: 10,
                            }
                          : {}
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <div className="p-4">
          Showing {paginatedData.length} of {filteredData.length} entries
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setCurrentPage((prev) => Math.max(prev - 1, 1));
            }}
            disabled={currentPage === 1}
            className={`py-1 px-3 border rounded ${
              currentPage === 1 ? "bg-gray-200 cursor-not-allowed" : "bg-white"
            }`}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {Math.ceil(sortedData.length / rowsPerPage)}
          </span>
          <button
            onClick={() => {
              setCurrentPage((prev) => prev + 1);
            }}
            disabled={
              currentPage === Math.ceil(sortedData.length / rowsPerPage)
            }
            className={`py-1 px-3 border rounded ${
              currentPage === Math.ceil(sortedData.length / rowsPerPage)
                ? "bg-gray-200 cursor-not-allowed"
                : "bg-white"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportTable;
